import { browserLocalStorage } from "@base/browserLocalStorage";
import { getBrowserReadings } from "@base/services/browserReadings";
import { postWebSession, UpsertWebSessionInput } from "@base/services/coreapi";
import { getFunnelIDFromURL } from "@base/utils/getFunnelIDFromURL";
import { getSiteConfig } from "@base/config";

/**
 * Web-session lifecycle service — the single owner of the session id (sid).
 *
 * Every session upsert must go through `upsertWebSession` here (not the raw
 * coreapi transport): it decorates the request with the right sid and, on
 * success, performs ALL persistence side effects, so no caller can end up with
 * a confirmed session that was never persisted.
 *
 * ## Cross-domain handoff (funnel domain → checkout domain)
 *
 * The funnel appends `?sid=<WebSession.cookieId>` when redirecting to a checkout
 * hosted on another registrable domain, because neither the api-domain sid
 * cookie nor localStorage travel across domains. On arrival the sid flows into
 * every `POST /session` body (`getSid`), which makes the backend continue the
 * funnel's session and re-set its own sid cookie.
 *
 * Storage is deliberately layered — localStorage alone is NOT trusted (Safari
 * private mode, quota, ITP script-storage caps are why web sessions moved to a
 * cookie in the first place):
 *
 *   1. URL param      — the durable carrier; only stripped after the backend
 *                       confirms the session (first successful upsert).
 *   2. in-memory      — survives storage failures within the pageload.
 *   3. sessionStorage — refresh-safety cache between arrival and confirmation;
 *                       best-effort only.
 *
 * After confirmation the api-domain cookie (HTTP-set, same-site with checkout)
 * and the localStorage webSession mirror both carry the sid independently —
 * either surviving is enough.
 */

const SID_PARAM = "sid";
const SESSION_CACHE_KEY = "nt_handoff_sid";

/** Fixed for the pageload once read; hydration needs it even after confirmation. */
let arrivalSid: string | undefined;
/** cookieId confirmed by a successful upsert this pageload — the freshest truth. */
let persistedSid: string | undefined;
let consumed = false;

function safeSessionStorage(): Storage | undefined {
  try {
    if (typeof window !== "undefined" && window.sessionStorage)
      return window.sessionStorage;
  } catch {
    // Storage access itself can throw (privacy modes / iframes)
  }
  return undefined;
}

function consumeFromURL(): void {
  if (consumed || typeof window === "undefined") return;
  consumed = true;

  const fromURL = new URLSearchParams(window.location.search).get(SID_PARAM);
  if (fromURL) {
    arrivalSid = fromURL;
    try {
      safeSessionStorage()?.setItem(SESSION_CACHE_KEY, fromURL);
    } catch {
      // best-effort; the URL param remains the carrier
    }
    return;
  }

  try {
    arrivalSid = safeSessionStorage()?.getItem(SESSION_CACHE_KEY) ?? undefined;
  } catch {
    arrivalSid = undefined;
  }
}

/**
 * The sid to send with every web session upsert. Precedence: backend-confirmed
 * sid (this pageload) > handoff arrival sid > webSession stored by earlier
 * visits on this domain. An explicit cross-domain arrival must beat a stale
 * local session — mirrors the backend's body-sid-over-cookie precedence.
 * Undefined is fine: same-site callers fall back to the api-domain cookie.
 */
export function getSid(): string | undefined {
  consumeFromURL();
  return (
    persistedSid ??
    arrivalSid ??
    browserLocalStorage.get().webSession?.cookieId ??
    undefined
  );
}

/**
 * The backend confirmed (or re-issued) the session. From here the api-domain
 * cookie + the localStorage webSession mirror own the sid, so the handoff cache
 * is dropped and `?sid=` stripped from the URL (it should not leak into shared
 * links or analytics page URLs longer than necessary).
 */
function markSessionPersisted(cookieId: string): void {
  persistedSid = cookieId;

  try {
    safeSessionStorage()?.removeItem(SESSION_CACHE_KEY);
  } catch {
    // ignore
  }

  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (url.searchParams.has(SID_PARAM)) {
    url.searchParams.delete(SID_PARAM);
    window.history.replaceState(window.history.state, "", url.toString());
  }
}

/**
 * Upsert the web session and persist the confirmed sid.
 *
 * `sid` continues the session when the api-domain cookie can't (cross-domain
 * handoff arrival, or browsers withholding the cookie); the backend gives the
 * body sid precedence over the cookie. Old backends strip the unknown prop.
 *
 * Fire-and-forget safe: all side effects run in here, so even a caller that
 * ignores the response (e.g. tracking init) leaves the session fully persisted.
 * React state mirrors (RootStore) still sync from the returned response.
 */
/**
 * Upserts are serialized: on a first visit (no sid anywhere yet) two
 * near-simultaneous calls — tracking init at client entry plus a page-level
 * upsert — would both go out sid-less and mint two backend sessions, splitting
 * attribution between them. Chaining lets the first call confirm a sid that
 * every queued call then reuses via `getSid()`.
 */
let lastUpsert: Promise<unknown> = Promise.resolve();

/**
 * Re-upsert when the visitor comes back to this page without a reload, so the
 * backend sees the return and `h` stays current:
 *
 *  - the tab was hidden for 30 s or more (checkout legal links open in a new
 *    tab; 30 s is the backend's minimum legal-page dwell), or
 *  - the page was restored from the back/forward cache (no pageload, so
 *    `onClientEntry` does not run again).
 *
 * Call once per pageload.
 */
const REFRESH_AFTER_HIDDEN_MS = 30_000;
let returnWatchStarted = false;

export function refreshWebSessionOnReturn(): void {
  if (returnWatchStarted || typeof document === "undefined") return;
  returnWatchStarted = true;

  // A back/forward-cache restore fires both pageshow and visibilitychange, in
  // no guaranteed order: collapse them into one refresh.
  let lastRefreshAt = 0;
  const refresh = () => {
    if (Date.now() - lastRefreshAt < 2_000) return;
    lastRefreshAt = Date.now();
    upsertWebSession().catch((err) => {
      console.error("Failed to refresh web session:", err);
    });
  };

  let hiddenAt: number | undefined;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      return;
    }
    const wasAway =
      hiddenAt !== undefined && Date.now() - hiddenAt >= REFRESH_AFTER_HIDDEN_MS;
    hiddenAt = undefined;
    if (wasAway) refresh();
  });

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) refresh();
  });
}

/**
 * Resolves once the upserts queued so far (on a pageload: the one from
 * tracking init) have settled, or after SETTLE_TIMEOUT_MS — whichever is
 * first. Memoized per pageload, so later callers (in-app navigation) resolve
 * at once. UI that varies by `h` waits on this instead of rendering on a
 * possibly stale mirror and then switching.
 */
const SETTLE_TIMEOUT_MS = 3_000;
let pageloadSettled: Promise<void> | undefined;
let isPageloadSettled = false;

export function whenWebSessionSettled(): Promise<void> {
  pageloadSettled ??= Promise.race([
    lastUpsert,
    new Promise((resolve) => setTimeout(resolve, SETTLE_TIMEOUT_MS)),
  ]).then(() => {
    isPageloadSettled = true;
  });
  return pageloadSettled;
}

/** Sync twin of `whenWebSessionSettled`: false until that has resolved. */
export function isWebSessionSettled(): boolean {
  return isPageloadSettled;
}

/**
 * True once an upsert succeeded this pageload, i.e. the localStorage mirror
 * (and its `h`) is this pageload's backend answer, not a leftover from an
 * earlier visit.
 */
export function isWebSessionConfirmed(): boolean {
  return persistedSid !== undefined;
}

export function upsertWebSession(
  input: Omit<UpsertWebSessionInput, "url"> = {},
) {
  // No backend configured (GATSBY_CORE_URL unset, e.g. the
  // truelovelica.com fake-door site): skip instead of failing on every page view.
  if (!getSiteConfig().coreApiHost) {
    return Promise.resolve({ data: null, error: null } as unknown as Awaited<
      ReturnType<typeof postWebSession>
    >);
  }

  // URL and funnel captured at call time (they describe the page that asked
  // for the upsert); sid resolved at execution time (it may be confirmed by a
  // queued-ahead upsert).
  const url = window.location.href;
  const funnelId = getFunnelIDFromURL();

  const run = async () => {
    const body: UpsertWebSessionInput = {
      ...input,
      url,
      funnelId,
      sid: getSid(),
      br: await getBrowserReadings(),
    };

    const res = await postWebSession(body);

    if (!res.error && res.data) {
      markSessionPersisted(res.data.cookieId);
      // Durable mirror for future pageloads on this domain (`getSid` fallback
      // layer). Merge-from-storage keeps concurrent write-through updates
      // intact.
      browserLocalStorage.set({
        ...browserLocalStorage.get(),
        webSession: res.data,
      });
    }

    return res;
  };

  const result = lastUpsert.then(run, run);
  lastUpsert = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}
